import React, { useState } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  TextField, 
  Button, 
  Paper,
  Avatar,
  Alert
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { changePassword } from '../services/api/auth'; // Import the changePassword function
import { showAlert } from '../components/service/AlertService';

const Profile = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [passwords, setPasswords] = useState({
    current: '',
    new: '',
    confirm: ''
  });
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [loading, setLoading] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    
    if (!passwords.current) {
      newErrors.current = t('profile.errors.currentPasswordRequired');
    }
    
    if (!passwords.new) {
      newErrors.new = t('profile.errors.newPasswordRequired');
    } else if (passwords.new.length < 8) {
      newErrors.new = t('profile.errors.passwordLength');
    }
    
    if (!passwords.confirm) {
      newErrors.confirm = t('profile.errors.confirmPasswordRequired');
    } else if (passwords.new !== passwords.confirm) {
      newErrors.confirm = t('profile.errors.passwordsDontMatch');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePasswordChange = (field) => (event) => {
    setPasswords(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    setErrors(prev => ({
      ...prev,
      [field]: ''
    }));
    setSuccess(false);
    setApiError(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSuccess(false);
    setApiError(null);

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const response = await changePassword(passwords.current, passwords.new);
      if (response.message === 'PASSWORD_CHANGED_SUCCESSFULLY') {
        setSuccess(true);
        showAlert('PASSWORD_CHANGED_SUCCESSFULLY');
        setPasswords({ current: '', new: '', confirm: '' });
      }
    } catch (error) {
      setApiError(error.message || t('profile.errors.apiError'));
      showAlert('PASSWORD_CHANGE_FAILED');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 12, mb: 4 }}>
      <Typography 
        variant="h4" 
        component="h1" 
        sx={{ 
          mb: 4,
          fontWeight: 300,
          letterSpacing: '0.1em'
        }}
      >
        {t('profile.name')}
      </Typography>

      <Paper 
        elevation={0}
        sx={{ 
          p: 4,
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(10px)',
          borderRadius: 2,
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
          <Avatar 
            sx={{ 
              width: 80, 
              height: 80,
              bgcolor: 'primary.main',
              fontSize: '2rem',
              mr: 3
            }}
          >
            {user?.username[0].toUpperCase()}
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 300 }}>
              {user?.username}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {user?.email}
            </Typography>
          </Box>
        </Box>

        <Typography 
          variant="h6" 
          sx={{ 
            mb: 3,
            fontWeight: 300,
            color: 'primary.main'
          }}
        >
          {t('profile.changePassword')}
        </Typography>

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {t('profile.passwordChanged')}
          </Alert>
        )}

        {apiError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {apiError}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <TextField
            margin="normal"
            required
            fullWidth
            name="currentPassword"
            label={t('profile.currentPassword')}
            type="password"
            value={passwords.current}
            onChange={handlePasswordChange('current')}
            error={!!errors.current}
            helperText={errors.current}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(255, 255, 255, 0.03)',
              }
            }}
            disabled={loading}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            name="newPassword"
            label={t('profile.newPassword')}
            type="password"
            value={passwords.new}
            onChange={handlePasswordChange('new')}
            error={!!errors.new}
            helperText={errors.new}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(255, 255, 255, 0.03)',
              }
            }}
            disabled={loading}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            name="confirmPassword"
            label={t('profile.confirmPassword')}
            type="password"
            value={passwords.confirm}
            onChange={handlePasswordChange('confirm')}
            error={!!errors.confirm}
            helperText={errors.confirm}
            sx={{
              mb: 3,
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(255, 255, 255, 0.03)',
              }
            }}
            disabled={loading}
          />
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            sx={{
              textTransform: 'none',
              letterSpacing: '0.1em',
              fontWeight: 300,
              background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
              '&:hover': {
                background: 'linear-gradient(45deg, #2196F3 60%, #21CBF3 90%)',
              },
            }}
          >
            {loading ? t('profile.updating') : t('profile.updatePassword')}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default Profile;